import {
    GameUIMessageType,
    BetOptionItem,
    FreespinsStatusChangedMessage,
    FeatureStatusChangedMessage,
    BetResponseReceivedMessage,
    BetRequestedMessage,
    SetSpeedButtonLevelMessage,
} from 'game-ui-types'
import { useUltraPlayStore, ULTRA_PLAY_BUY_FREESPINS } from '@stores/ultraPlayStore'
import { useConfigurationStore, usePlayerInfoStore, useSpinButtonSectionStore } from '@stores'
import { closeUltraPlay } from './ultraPlayClose'
import { flushPendingUltraPlayPause } from './ultraPlayPause'
import { collectUltraPlayWin } from './ultraPlayCollect'
import { ultraPlayAnalytics } from './ultraPlayAnalytics'

// Engine-sync logic for Ultra Play, extracted from App.tsx's window 'message' handler so it can be
// unit-tested in isolation.
//
// Every recorder row comes from ONE engine message: BET_RESPONSE_RECEIVED, whose payload carries the
// round's authoritative `winAmount` and the resulting `balance` (stake already deducted, win already
// credited). One response is emitted per round — base spins and free spins alike — so a row is written
// exactly when a round resolves, with the engine's own numbers. Everything the recorder used to derive
// from the live balance and the win-amount counter (the shift model, the win arm/disarm heuristic,
// deferred pause finalizes, late-win corrections and rejected-bet rollbacks) is gone with it.
//
// What is left besides recording:
//   - Feature lifecycle (free spins): group the feature's responses as sub-rows under the triggering
//     row and finish the feature on the first base count after it.
//   - Autoplay batching: honour the requested Ultra Play count across the engine's per-batch cap, stop
//     the engine at the target, and end the session once the last round has reported.
//   - Insufficient balance handling.
//
// All store reads/writes go through the real stores (as in App); only outgoing engine messages are
// injected via `send`, so tests can drive real store state and assert on it while spying on `send`.

export type SendMessage = (message: unknown) => void

const FEATURE_BET_STATES = ['freespin', 'hold_and_spin', 'pre_freespin', 'regular_wheel', 'big_wheel']

const defaultSend: SendMessage = (message) => window.uiBus.postMessage(message)

// Index of the largest FINITE engine autoplay option. Used for infinite Ultra Play: the engine's
// endless option emits no per-spin count, so infinite is driven as repeated finite batches instead.
export const largestFiniteEngineIndex = (engineOptions: number[]): number => {
    let best = -1

    engineOptions.forEach((count, i) => {
        if (count !== -1 && (best === -1 || count > engineOptions[best])) best = i
    })

    return best === -1 ? 0 : best
}

export const createUltraPlaySpinRecorder = (sendArg?: SendMessage) => {
    const send: SendMessage = sendArg ?? defaultSend

    // Spin that triggered the active feature — promoted to the "+N" group marker.
    let featureParentSpin: number | null = null
    // Latest total free spins count from the free spins status message — used for the "+N" badge.
    let featureCount = 0
    // Set after a feature completes — skips the resumeAutoplay() count-update message (a non-spin).
    let skipNextSpinCount = false
    // Last engine autoplay count (UPDATE_AUTOPLAY_COUNT payload). 0 means the engine's batch finished
    // naturally; >0 at AUTOPLAY_STOPPED means it paused mid-batch for a feature.
    let lastEnginePayload = -1

    let pendingBet: { bet: number; isFreespin: boolean } | null = null

    /**
     * Balance from the most recent bet response
     */
    let lastReportedBalance: number | null = null

    /**
     * The feature started while its triggering round was still awaiting its response. The response
     * writes the trigger row, so the promotion to the "+N" marker waits for it — otherwise the marker
     * would land on the PREVIOUS row and the feature's spins would be grouped under the wrong bet.
     */
    let pendingFeaturePromotion: string | null = null

    /**
     * AUTOPLAY_STOPPED arrived while a round was still awaiting its response. The stop decision (end the
     * session, re-trigger the next batch, …) reads `remainingSpins`, which that response still has to
     * decrement — so it is deferred until the row lands.
     */
    let isStopDecisionDeferred = false

    /**
     * The last Ultra Play spin has triggered a feature (FS).
     * We can't enter READY state until completeFeature is called.
     */
    let isSessionCompleteDelayedByFeature = false

    /**
     * The engine's autoplay was stopped for the running feature, so it will not resume by itself when
     * the feature ends — completeFeature() starts the next batch instead.
     */
    let didStopEngineAutoplayForFeature = false

    const setPendingBet = (bet: typeof pendingBet) => {
        pendingBet = bet
        useUltraPlayStore.getState().setHasUnrecordedBet(bet !== null && !bet.isFreespin)
    }

    const hasPendingBaseGameBet = () => pendingBet !== null && !pendingBet.isFreespin

    const getBet = (): number => {
        const { selectedBetIndex } = useUltraPlayStore.getState()
        const { betOptions } = useConfigurationStore.getState()
        const betOption = betOptions[selectedBetIndex]

        return betOption === undefined
            ? 0
            : typeof betOption === 'number'
              ? betOption
              : (betOption as BetOptionItem).betAmount
    }

    // Promote the most recent row to the feature's "+N" group marker; the feature's spins become its
    // sub-rows. The jackpot keeps its trigger a plain row (no star) but still collects its win on it.
    const promoteTriggerRow = (featureName: string) => {
        const up = useUltraPlayStore.getState()
        const triggerSpin = up.spins[0]

        if (!triggerSpin) {
            // No triggering row to promote (edge case) — the first feature response creates a marker.
            featureParentSpin = null

            return
        }

        if (featureName !== 'jackpot') {
            up.updateSpin(triggerSpin.spinNumber, { type: 'FREE_SPIN', isExpanded: true })
        }

        featureParentSpin = triggerSpin.spinNumber
    }

    let nextMarkerSpinNumber = -1

    const enterFeature = (featureName: string) => {
        useUltraPlayStore.getState().setIsInFeature(true)
        featureCount = 0

        if (hasPendingBaseGameBet()) {
            pendingFeaturePromotion = featureName
        } else {
            promoteTriggerRow(featureName)
        }
    }

    // A base-game round resolved — write its row straight from the engine's own numbers.
    const recordBaseSpin = (payload: BetResponseReceivedMessage['payload']) => {
        const up = useUltraPlayStore.getState()
        const bet = pendingBet?.bet ?? getBet()
        const win = Math.max(0, payload.winAmount)
        const spinNumber = up.currentSpinNumber + 1

        up.addSpin({
            spinNumber,
            bet,
            win,
            winMultiplier: bet > 0 ? win / bet : 0,
            balance: payload.balance,
            type: 'NORMAL',
        })
        up.decrementRemainingSpins()

        // This was the feature's triggering round — now that its row exists, it can carry the marker.
        if (pendingFeaturePromotion !== null) {
            const featureName = pendingFeaturePromotion

            pendingFeaturePromotion = null
            promoteTriggerRow(featureName)
        }
    }

    // A round inside a feature resolved — write it as a sub-row of the feature's marker. Free spins
    // don't consume the session's spin count, so `remainingSpins` is untouched.
    const recordFeatureSpin = (payload: BetResponseReceivedMessage['payload']) => {
        const up = useUltraPlayStore.getState()
        const win = Math.max(0, payload.winAmount)

        if (featureParentSpin === null) {
            // No triggering spin to promote (edge case) — create a standalone marker to group under.
            // It is not a paid round, so it must not advance the spin counter (see addFeatureMarkerSpin);
            // it still needs an id, because sub-rows attach by spinNumber, hence the negative sequence.
            const spinNumber = nextMarkerSpinNumber

            nextMarkerSpinNumber -= 1

            up.addFeatureMarkerSpin(
                {
                    spinNumber,
                    bet: 0,
                    win: 0,
                    winMultiplier: 0,
                    balance: payload.balance,
                    type: 'FREE_SPIN',
                    freeSpinsAwarded: featureCount || undefined,
                },
                true
            )
            featureParentSpin = spinNumber
        }

        up.addSubSpin(featureParentSpin, {
            win,
            winMultiplier: 0,
            balance: payload.balance,
            type: 'FREE_SPIN',
        })
    }

    /**
     * Whether another spin can still be paid for out of the balance the engine just reported.
     *
     * Read straight from the response — the engine's own post-round value — rather than from the
     * animated balance in playerInfoStore, which lags and mid-tween reads the wrong number.
     */
    const canAffordNextSpin = (balance: number): boolean => {
        const up = useUltraPlayStore.getState()

        // Nothing more will be charged: the session is over, paused, or the feature plays for free.
        if (up.status !== 'PLAYING' || up.isInFeature || up.remainingSpins === 0) return true

        const bet = getBet()

        return bet <= 0 || balance >= bet
    }

    // The balance can no longer cover the next bet (the round's own stake deduction is usually what
    // brings it below, but a server balance sync does it too). The engine crashes if it then attempts an
    // unaffordable autoplay spin, so stop it and prompt the player to top up (the session stays paused).
    const blockOnInsufficientBalance = () => {
        const up = useUltraPlayStore.getState()

        send({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED })
        up.pause()
        up.setAwaitingAutoplayStop(true)
        up.setShowInsufficientBalance(true)
    }

    // Re-issue autoplay in batches so the requested Ultra Play count is honoured regardless of the
    // engine's per-batch autoplay cap (autoplayLimit).
    const retriggerAutoplay = (remaining: number) => {
        const engineOptions = useConfigurationStore.getState().autoplayOptions
        // Infinite (-1): run the largest finite batch (endless emits no per-spin count); the -1
        // remaining marker re-triggers it forever. Otherwise pick the closest finite option.
        const index =
            remaining === -1
                ? largestFiniteEngineIndex(engineOptions)
                : engineOptions.reduce((best: number, count: number, i: number) => {
                      if (count === -1) return best

                      const bestCount = engineOptions[best] === -1 ? Infinity : engineOptions[best]

                      return Math.abs(count - remaining) < Math.abs(bestCount - remaining) ? i : best
                  }, 0)

        send({ type: GameUIMessageType.AUTOPLAY_BUTTON_CLICKED, payload: index })
        lastEnginePayload = -1

        const sbStore = useSpinButtonSectionStore.getState()

        sbStore.updateAutoplayMode(true)
        sbStore.updateAutoplayCount(remaining === -1 ? -1 : remaining - 1)
        sbStore.updateSelectedAutoplayIndex(index)
    }

    // What to do now that the engine's batch has really stopped and every started round has reported:
    // continue with the next batch or end the session. An unaffordable balance has already paused the
    // session from the response that revealed it (see canAffordNextSpin), so neither branch runs then.
    const resolveAutoplayStop = () => {
        const up = useUltraPlayStore.getState()

        // A user pause — nothing to continue; Play decides what happens next.
        if (up.status !== 'PLAYING') return

        // The last autoplay round won a feature.
        if (up.isInFeature) {
            isSessionCompleteDelayedByFeature = true

            return
        }

        const previousSpeedLevel = useUltraPlayStore.getState().previousSpeedLevel

        window.uiBus.postMessage({
            type: GameUIMessageType.SET_SPEED_BUTTON_LEVEL,
            speedLevel: previousSpeedLevel,
        } as SetSpeedButtonLevelMessage)

        if (up.isPausePending) {
            flushPendingUltraPlayPause()

            return
        }

        if (up.pendingAutoplayRestart) {
            // Spins count changed mid-session — restart immediately with the new count.
            up.setPendingAutoplayRestart(false)
            retriggerAutoplay(up.remainingSpins)

            return
        }

        if (up.remainingSpins === 0) {
            // Natural completion (finite count reached 0) — stop but preserve spins for history.
            // Infinite (-1) never hits this; it re-triggers below to keep going.
            ultraPlayAnalytics.sessionEnded('completed')
            up.stop()

            return
        }

        // Spins still remain. Use the engine's last autoplay counter to disambiguate:
        //  - payload === 0  → the batch finished at its cap → continue with the next batch
        //  - payload  >  0  → the engine paused mid-batch for a feature (FEATURE_STATUS_CHANGED
        //                     'started' arrives a tick later) → keep the session alive; it resumes itself
        if (lastEnginePayload === 0) {
            retriggerAutoplay(up.remainingSpins)
        }
    }

    // Ends the feature view in Ultra Play: collapse the "+N" marker and clear feature state. Called
    // either from FEATURE_STATUS_CHANGED 'completed' or — earlier and more reliably — from the first
    // base autoplay count after the feature (free spins emit no autoplay counts, so a count means the
    // base game has resumed; the 'completed' message is tied to the outro animation and lags when the
    // outro is skipped). Returns true if it actually cleared an active feature.
    const completeFeature = (): boolean => {
        const up = useUltraPlayStore.getState()

        if (!up.isInFeature) return false

        if (featureParentSpin !== null) {
            up.setSpinExpanded(featureParentSpin, false)
        }

        up.setIsInFeature(false)
        up.setBuyBonusRoundInFlight(false)
        featureParentSpin = null
        featureCount = 0
        pendingFeaturePromotion = null
        setPendingBet(null)

        const shouldRestartEngineAutoplay = didStopEngineAutoplayForFeature

        didStopEngineAutoplayForFeature = false

        // The engine had already stopped and the session was only held open for this feature.
        if (isSessionCompleteDelayedByFeature) {
            isSessionCompleteDelayedByFeature = false
            resolveAutoplayStop()

            return true
        }

        // Its autoplay was stopped when the feature started, so nothing resumes on its own — continue
        // the session with the next batch.
        const { status, remainingSpins } = useUltraPlayStore.getState()

        if (shouldRestartEngineAutoplay && status === 'PLAYING' && remainingSpins !== 0) {
            retriggerAutoplay(remainingSpins)
        }

        return true
    }

    return {
        // Drop any pending bet when a session ends (→ READY) so the next session starts clean.
        resetPending: () => {
            setPendingBet(null)
            pendingFeaturePromotion = null
            isStopDecisionDeferred = false
            isSessionCompleteDelayedByFeature = false
            didStopEngineAutoplayForFeature = false
            lastEnginePayload = -1
            lastReportedBalance = null
        },

        /**
         * The engine received the server's response for a round — the single source of every row.
         *
         * `winAmount` is the round's win and `balance` the resulting balance (stake deducted, win
         * credited), so the row needs nothing but the payload and the bet the round was placed with.
         */
        onBetResponse: (payload: BetResponseReceivedMessage['payload']) => {
            const up = useUltraPlayStore.getState()

            if (up.status !== 'PLAYING' && up.status !== 'PAUSED') return

            if (payload.isCancelled) {
                send({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED })
                ultraPlayAnalytics.sessionEnded('transfer_cancelled')
                up.stop()

                return
            }

            const isFeature = pendingBet ? pendingBet.isFreespin : up.isInFeature

            lastReportedBalance = payload.balance

            if (isFeature) {
                recordFeatureSpin(payload)
            } else {
                recordBaseSpin(payload)
            }

            setPendingBet(null)

            if (!isFeature && FEATURE_BET_STATES.includes(payload.state.toLowerCase())) enterFeature(payload.state)

            if (!canAffordNextSpin(payload.balance)) {
                blockOnInsufficientBalance()

                return
            }

            // The engine had already confirmed its stop and was only waiting on this row.
            if (isStopDecisionDeferred) {
                isStopDecisionDeferred = false
                resolveAutoplayStop()
            }
        },

        onBetRequested: (payload: BetRequestedMessage['payload']) => {
            const up = useUltraPlayStore.getState()

            if (up.status !== 'PLAYING' && up.status !== 'PAUSED') return

            // A bet while PAUSED means the engine already started this round before the stop
            // reached it. Go back to PLAYING so it plays out, and pause once it is over.
            // STEAL-8882
            if (up.status === 'PAUSED' && !payload.isFreespin && !up.showFeatureChoiceDialog) {
                up.setStatus('PLAYING')
                up.setPausePending(true)
            }

            setPendingBet({ bet: payload.bet, isFreespin: payload.isFreespin })
        },

        // Unified feature lifecycle for Ultra Play (started / completed).
        onFeatureStatusChanged: (payload: FeatureStatusChangedMessage['payload']) => {
            const up = useUltraPlayStore.getState()

            const isBeforeSessionStart = up.status === 'READY' && up.isOpen

            // Only relevant while an Ultra Play session is running, or just after it was opened
            if (up.status !== 'PLAYING' && up.status !== 'PAUSED' && !isBeforeSessionStart) return

            if (payload.status === 'started') {
                up.setCurrentFeatureName(payload.name)

                const isAutoplayable = !!payload.isAutoplayable

                // Stop on Feature: don't play through and don't show the choice dialog. Many features
                // can't be paused and would start in the background behind the dialog, so the product
                // decision is to close Ultra Play entirely (same cleanup as a manual close) and let the
                // player play the feature in the game.
                if (up.stopOnFeature) {
                    ultraPlayAnalytics.featureTriggered(payload.name, !!payload.isAutoplayable)
                    closeUltraPlay('stop_on_feature')

                    return
                }

                // When we open Ultra Play, we collect the last win
                // If it's a freespins win, Lucky Spins arrive with the collect response,
                // We need to allow the feature to start before the session is started
                if (isBeforeSessionStart) {
                    ultraPlayAnalytics.featureTriggered(payload.name, isAutoplayable)
                    up.setFeatureDialogAutoplayable(false)
                    up.setShowFeatureChoiceDialog(true)
                    ultraPlayAnalytics.featureDialogShown(payload.name, false)

                    return
                }

                // Guarded like enterFeature itself: a repeated 'started' for an already-running feature
                // must not count as a second trigger.
                if (!up.isInFeature) {
                    ultraPlayAnalytics.featureTriggered(payload.name, isAutoplayable)
                    enterFeature(payload.name)
                }

                const shouldAutoplayThrough = !up.stopOnFeature && isAutoplayable

                if (!shouldAutoplayThrough && !up.showFeatureChoiceDialog) {
                    // Cases 1.1 / 1.2 / 2.2 — pause and present the choice dialog.
                    up.setFeatureDialogAutoplayable(isAutoplayable)
                    ultraPlayAnalytics.sessionPaused('feature')
                    up.pause()
                    up.setShowFeatureChoiceDialog(true)
                    ultraPlayAnalytics.featureDialogShown(payload.name, isAutoplayable)

                    // collect the last win before the feature starts, so the player sees it in the dialog
                    if (payload.name === 'luckySpins') {
                        collectUltraPlayWin()
                    }
                } else if (useUltraPlayStore.getState().status === 'PLAYING') {
                    // Case 2.1 — play through silently; each free spin lands as a sub-row on its response.
                    // The engine only PAUSES its autoplay for a feature and resumes it the instant the
                    // feature ends, charging a round before a close or a pause can reach it (the splashes
                    // are skipped here, so the engine runs well ahead of what the player sees). Stop it
                    // now; completeFeature() starts the next batch if the session is still running.
                    didStopEngineAutoplayForFeature = true
                    send({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED })
                }
            } else if (payload.status === 'completed') {
                up.setCurrentFeatureName('')

                const dialogState = useUltraPlayStore.getState()

                if (dialogState.showFeatureChoiceDialog && !dialogState.featureDialogAutoplayable) {
                    dialogState.setShowFeatureChoiceDialog(false)
                    completeFeature()
                    closeUltraPlay('non_autoplayable_feature_completed')

                    return
                }

                // Fallback path — if the base game hasn't resumed yet (so the count handler didn't
                // already finish the feature), clear it here and skip the upcoming resume-artifact count.
                if (completeFeature()) {
                    skipNextSpinCount = true
                }

                const balance = lastReportedBalance ?? usePlayerInfoStore.getState().balance

                if (!up.isBetInFlight && !canAffordNextSpin(balance)) {
                    blockOnInsufficientBalance()

                    return
                }
            }
            // 'updated' — ignored for now
        },

        // Free spin counts drive the "+N" badge and retrigger markers only; the rows themselves are
        // written by each free spin's own bet response.
        onFreespinCount: (data: FreespinsStatusChangedMessage['payload']) => {
            const up = useUltraPlayStore.getState()

            if (up.status !== 'PLAYING' && up.status !== 'PAUSED') return

            if (!up.isInFeature) return

            // A retrigger raises totalFreespins mid-feature (e.g. 15 → 25). The increase belongs to
            // the free spin currently being played; show "+delta" on its row.
            const prevTotal = featureCount
            const retriggerDelta =
                prevTotal > 0 && data.totalFreespins > prevTotal ? data.totalFreespins - prevTotal : 0

            featureCount = data.totalFreespins

            // Keep the "+N" marker total in sync (initial award + any retriggers).
            if (featureParentSpin !== null) {
                up.updateSpin(featureParentSpin, { freeSpinsAwarded: data.totalFreespins })

                if (retriggerDelta > 0) up.addFeatureRetrigger(featureParentSpin, retriggerDelta)
            }
        },

        onAutoplayIssued: () => {
            lastEnginePayload = -1
            isStopDecisionDeferred = false
            skipNextSpinCount = false
        },

        onAutoplayCount: (payload: number | undefined) => {
            const previousEnginePayload = lastEnginePayload

            // Track the engine's autoplay counter so AUTOPLAY_STOPPED can tell a finished batch
            // (payload reached 0) from a mid-batch feature pause (payload still > 0).
            lastEnginePayload = Number(payload ?? -1)

            const countRepeated = previousEnginePayload !== -1 && lastEnginePayload === previousEnginePayload

            // Skip the count-update message emitted by resumeAutoplay() after a feature ends
            if (skipNextSpinCount) {
                skipNextSpinCount = false

                return
            }

            const up = useUltraPlayStore.getState()

            if (up.status === 'READY') {
                setPendingBet(null)

                return
            }

            if (up.isInFeature) {
                if (up.status === 'PLAYING') completeFeature()

                return
            }

            if (up.status === 'PAUSED') return

            // Target reached — stop the engine before it overruns.
            if (up.remainingSpins === 0) {
                send({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED })

                // The engine re-asserted its counter without charging anything — the session is done.
                if (countRepeated) {
                    setPendingBet(null)
                    ultraPlayAnalytics.sessionEnded('completed')
                    up.stop()
                }

                return
            }

            const buyBonus = up.selectedBuyBonusFeatureName

            // TODO: Consider sending a list of features from the engine to handle other multi-spin
            // bonus purchases.
            const isBuyBonusRound = buyBonus === ULTRA_PLAY_BUY_FREESPINS

            if (up.isBuyBonusRoundInFlight !== isBuyBonusRound) {
                up.setBuyBonusRoundInFlight(isBuyBonusRound)
            }

            // This count started the last wanted spin — stop the engine now so it doesn't charge one
            // more while this one plays out.
            if (up.remainingSpins === 1 && lastEnginePayload > 0 && !useUltraPlayStore.getState().isInFeature) {
                send({ type: GameUIMessageType.STOP_AUTOPLAY_CLICKED })
            }
        },

        onAutoplayStopped: () => {
            const up = useUltraPlayStore.getState()

            // A feature is running — this is the stop sent when it started (or the engine's own pause).
            // Either way nothing continues until the feature is over: completeFeature() restarts the
            // next batch, or, when the session's spins are already used up, ends the session.
            if (up.isInFeature) {
                if (up.status === 'PLAYING' && up.remainingSpins === 0) isSessionCompleteDelayedByFeature = true

                return
            }

            // READY: already cleaned up, nothing outstanding.
            if (up.status === 'READY') return

            // A charged round hasn't reported yet — its row still has to land (and decrement the
            // remaining count) before the stop can be interpreted. Defer to that response.
            if (hasPendingBaseGameBet()) {
                isStopDecisionDeferred = true

                return
            }

            setPendingBet(null)
            resolveAutoplayStop()
        },
    }
}

export type UltraPlaySpinRecorder = ReturnType<typeof createUltraPlaySpinRecorder>
